<template>
  <div class="pd-products-page">
    <!-- Search and Filter Bar -->
    <div class="pd-filter-bar">
      <div class="pd-container">
        <div class="pd-filter-content">
          <!-- Search -->
          <div class="pd-search-wrapper">
            <input
              type="text"
              class="pd-search-input"
              placeholder="搜尋商品..."
              v-model="filterInputText"
              @input="handleFilterProducts"
            />
            <i class="fas fa-search pd-search-icon"></i>
          </div>

          <!-- Category Filter -->
          <div class="pd-category-filter">
            <select
              class="pd-category-select"
              v-model="filterCategoryId"
              @change="handleFilterProducts"
            >
              <option :value="undefined">所有分類</option>
              <option
                v-for="category in categories"
                :key="category.id"
                :value="category.id"
              >
                {{ category.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Products Grid -->
    <div class="pd-container pd-products-container">
      <EcSpinner v-if="isLoading" />

      <div v-else-if="filterProducts.length > 0" class="pd-products-grid">
        <ProductsCard
          v-for="product in filterProducts"
          :key="product.id"
          :product="product"
        />
      </div>

      <div v-else class="pd-no-products">
        <p>沒有找到相關商品</p>
      </div>
    </div>
  </div>
</template>

<script>
import productsAPI from '@/apis/products'
import { Toast } from '@/utils/helpers'
import ProductsCard from '@/components/ProductsCard'
import EcSpinner from '@/components/EcSpinner'

export default {
  name: 'EcProducts',
  data () {
    return {
      categories: [],
      products: [],
      filterProducts: [],
      cart: {
        items: []
      },
      showSideCart: false,
      isLoading: true,
      filterInputText: '',
      filterCategoryId: undefined
    }
  },
  created () {
    this.fetchProducts()
  },
  mounted () {
    this.$root.$on('toggleSideCart', () => {
      this.showSideCart = !this.showSideCart
    })
  },
  methods: {
    async fetchProducts () {
      try {
        const response = await productsAPI.getProducts()

        if (response.statusText !== 'OK') {
          throw new Error(response.statusText)
        }

        this.products = response.data.products
        this.filterProducts = this.products
        this.cart = response.data.cart
        this.categories = response.data.categories
        this.$store.commit('setNavbarCartItemNumber', response.data.cart.items.length)

        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        Toast.fire({
          type: 'error',
          title: '無法取得商品資料，請稍後再試'
        })
      }
    },
    async handleDeleteItem (cartId, cartItemId) {
      try {
        const { statusText } = await productsAPI.deleteItem(cartId, cartItemId)

        if (statusText !== 'OK') {
          throw new Error(statusText)
        }

        this.fetchProducts()
      } catch (error) {
        Toast.fire({
          type: 'error',
          title: '無法刪除購物車商品，請稍後再試'
        })
      }
    },
    handleFilterProducts () {
      this.filterProducts = this.products

      // Filter by category
      if (this.filterCategoryId !== undefined) {
        this.filterProducts = this.filterProducts.filter(product => {
          return product.category_id === this.filterCategoryId
        })
      }

      // Filter by search text
      if (this.filterInputText !== '') {
        this.filterProducts = this.filterProducts.filter(product => {
          const searchText = this.filterInputText.toLowerCase()
          return (
            product.name.toLowerCase().includes(searchText) ||
            (product.categories && product.categories.name.toLowerCase().includes(searchText))
          )
        })
      }
    }
  },
  components: {
    ProductsCard,
    EcSpinner
  }
}
</script>

<style scoped>
.pd-products-page {
  min-height: 100vh;
  padding-top: var(--navbar-height);
}

/* Filter Bar */
.pd-filter-bar {
  background-color: var(--pd-white);
  border-bottom: 1px solid var(--pd-gray-light);
  padding: 2rem 0;
  margin-bottom: 3rem;
}

.pd-filter-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  justify-content: center;
}

/* Search Input */
.pd-search-wrapper {
  position: relative;
  flex: 1;
  max-width: 400px;
}

.pd-search-input {
  width: 100%;
  padding: 0.75rem 3rem 0.75rem 1rem;
  border: 2px solid var(--pd-black);
  background-color: var(--pd-white);
  font-size: 1rem;
  transition: all var(--transition-fast);
}

.pd-search-input:focus {
  outline: none;
  border-color: var(--pd-gray-dark);
}

.pd-search-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--pd-gray-medium);
  pointer-events: none;
}

/* Category Select */
.pd-category-filter {
  min-width: 200px;
}

.pd-category-select {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid var(--pd-black);
  background-color: var(--pd-white);
  font-size: 1rem;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.pd-category-select:focus {
  outline: none;
  border-color: var(--pd-gray-dark);
}

/* Products Container */
.pd-products-container {
  padding-bottom: 4rem;
}

/* Products Grid */
.pd-products-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
}

/* No Products */
.pd-no-products {
  text-align: center;
  padding: 4rem 0;
  font-size: 1.25rem;
  color: var(--pd-gray-medium);
}

/* Responsive */
@media (max-width: 1200px) {
  .pd-products-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .pd-filter-content {
    flex-direction: column;
    gap: 1rem;
  }

  .pd-search-wrapper,
  .pd-category-filter {
    width: 100%;
    max-width: none;
  }

  .pd-products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
}

@media (max-width: 480px) {
  .pd-products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  .pd-filter-bar {
    padding: 1.5rem 0;
    margin-bottom: 2rem;
  }
}
</style>
